import React, { Suspense, lazy, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import Navbar from './components/Navbar/Navbar';
import CheckoutHeader from './components/checkout/CheckoutHeader';
import Footer from './pages/Footer';
import ScrollToTop from './pages/ScrollToTop';
import Spinner from './components/spinner/Spinner';
import Seo from './seo/Seo';
import { routeTypeForPath } from './seo/metadata.mjs';

// The landing route is imported eagerly: lazy-loading it costs an extra network
// round trip before the hero can render, which directly delays LCP.
import HomePage from './pages/HomePage';

// Lazy loading components
const Signin = lazy(() => import('./components/auth/Signin'));
const Signup = lazy(() => import('./components/auth/Signup'));
const Product = lazy(() => import('./components/product/product'));
const Shop = lazy(() => import('./pages/Shop'));
const Cart = lazy(() => import('./components/cart/Cart'));
const Checkout = lazy(() => import('./components/checkout/Checkout'));
const OrderConfirmation = lazy(() => import('./components/order/OrderConfirmation'));
const About = lazy(() => import('./components/about/About'));
const Confirmation = lazy(() => import('./components/confirmation/Confirmation'));
const Privacy = lazy(() => import('./components/Legal/Privacy'));
const Terms = lazy(() => import('./components/Legal/Terms'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const SearchResults = lazy(() => import('./components/search/SearchResults'));
const ReviewInvite = lazy(() => import('./components/review/ReviewInvite'));
const NotFound = lazy(() => import('./pages/404/NotFound'));

// Deliberately renders Confirmation unconditionally. A missing session_id used to redirect
// silently to the homepage, which is how a paid customer ended up looking at the storefront with
// no sign their payment had succeeded - and why a broken return URL went unnoticed for so long.
// Confirmation already handles the missing case by showing "Payment Validation Failed", which
// gives the customer a support route and leaves a trace.
const ProtectedConfirmation = () => <Confirmation />;

function App() {
  const location = useLocation();

  // gtag.js is loaded once from index.html, which also fires the initial
  // page_view. Only route changes need to be reported from here.
  const isFirstView = React.useRef(true);
  useEffect(() => {
    if (isFirstView.current) {
      isFirstView.current = false;
      return;
    }
    window.gtag?.('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);

  // The server already put the right <head> on the HTML for the landing URL, but
  // client-side navigation never touches the server. This keeps the title,
  // canonical and JSON-LD in step as the visitor moves around.
  //
  // Product pages are excluded: ProductDetail renders its own <Seo> once the
  // product has loaded, because its tags depend on data this component has not
  // fetched. Two Helmets describing the same page would race.
  const routeType = routeTypeForPath(location.pathname);

  // The confirmation page is the end of the checkout flow, not part of the storefront: it carries
  // its own "Continue Shopping" and "Return Home" actions, so the store chrome adds nothing. It
  // also actively hurt - Navbar is `fixed`, .content has no top padding, and the page centres
  // itself over the full viewport, so the success tick sat underneath the navbar.
  const isCheckoutComplete = location.pathname === '/return';

  // Checkout keeps a header, unlike the page above, because it is mid-funnel: the customer may
  // still need to go back and change a quantity, and the page offers no route of its own.
  // CheckoutHeader drops the browsing links but keeps the way back. See its own comment.
  const isCheckout = location.pathname === '/checkout';

  return (
    <div className="app-container">
      {routeType !== 'product' && <Seo type={routeType} />}
      {isCheckout ? <CheckoutHeader /> : !isCheckoutComplete && <Navbar />}
      <ScrollToTop />
      <Suspense fallback={<Spinner />}>
        <div className="content">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<About />} />
            <Route path="/login" element={<Signin />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/search/:query" element={<SearchResults />} />
            <Route path="/return" element={<ProtectedConfirmation />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/register" element={<Signup />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/product/:id" element={<Product />} />
            <Route path="/review/:token" element={<ReviewInvite />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/terms-and-conditions" element={<Terms />} />
            <Route path="/orderid/:orderId" element={<OrderConfirmation />} />
            <Route path="/contact" element={<ContactPage/>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </Suspense>
      {!isCheckoutComplete && !isCheckout && <Footer />}
    </div>
  );
}

export default App;
